import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { prefersReducedMotion } from './hooks';

export interface ToastInput {
  title: string;
  description: string;
  icon: LucideIcon;
  tone?: 'ok' | 'muted' | undefined;
}
export interface Toast extends ToastInput {
  id: number;
  leaving: boolean;
}

const LIFETIME_MS = 4200;
const LEAVE_MS = 300;
let seq = 0;

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: ToastInput) => {
    seq += 1;
    const id = seq;
    setToasts((ts) => [{ ...t, id, leaving: false }, ...ts].slice(0, 4));
  }, []);
  const leave = useCallback((id: number) => {
    setToasts((ts) => ts.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(
      () => setToasts((ts) => ts.filter((t) => t.id !== id)),
      prefersReducedMotion() ? 0 : LEAVE_MS,
    );
  }, []);
  return { toasts, push, leave };
}

/**
 * Sonner-style stack: the newest toast sits in front, older ones peek out
 * behind it (smaller, higher). Hovering fans them out and pauses the
 * timers. One polite live region announces each title and description.
 */
export function ToastStack({
  toasts,
  onLeave,
}: {
  toasts: Toast[];
  onLeave: (id: number) => void;
}) {
  const [hover, setHover] = useState(false);
  const [shown, setShown] = useState<ReadonlySet<number>>(() => new Set());
  const heights = useRef(new Map<number, number>());
  const nodes = useRef(new Map<number, HTMLDivElement>());

  // Enter: mount below the stack, then move into place on the next frame.
  useLayoutEffect(() => {
    const fresh = toasts.filter((t) => !shown.has(t.id)).map((t) => t.id);
    if (fresh.length === 0) return;
    for (const id of fresh) {
      const n = nodes.current.get(id);
      if (n) heights.current.set(id, n.offsetHeight);
    }
    const reveal = () => setShown((s) => new Set([...s, ...fresh]));
    if (prefersReducedMotion()) {
      reveal();
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(reveal);
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [toasts, shown]);

  // Timers pause while hovered.
  useEffect(() => {
    if (hover) return;
    const timers = toasts
      .filter((t) => !t.leaving)
      .map((t) => window.setTimeout(() => onLeave(t.id), LIFETIME_MS));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [toasts, hover, onLeave]);

  let offset = 0;
  return (
    <div
      className="pf-toasts"
      aria-live="polite"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {toasts.map((t, i) => {
        const Icon = t.icon;
        let transform: string;
        let opacity = 1;
        if (!shown.has(t.id)) {
          transform = 'translateY(24px) scale(.96)';
          opacity = 0;
        } else if (hover) {
          transform = `translateY(${-offset + (t.leaving ? 12 : 0)}px)`;
          if (t.leaving) opacity = 0;
        } else {
          transform = `translateY(${-i * 10 + (t.leaving ? 12 : 0)}px) scale(${1 - i * 0.05})`;
          if (i > 2 || t.leaving) opacity = 0;
        }
        offset += (heights.current.get(t.id) ?? 64) + 8;
        return (
          <div
            key={t.id}
            ref={(n) => {
              if (n) nodes.current.set(t.id, n);
              else nodes.current.delete(t.id);
            }}
            className="pf-toast"
            data-tone={t.tone ?? 'ok'}
            style={{ transform, opacity, zIndex: 100 - i }}
          >
            <Icon aria-hidden="true" />
            <b>{t.title}</b>
            <span>{t.description}</span>
          </div>
        );
      })}
    </div>
  );
}
